// Custom hook for managing platform settings state
import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

export const useSettingsState = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initial default settings structure
  const getDefaultSettings = () => ({
    // Services carry no platform fee (utils/paymentSplitCalculator.js sets it to 0).
    platformFeePercentService: 0,
    stripeFeePercent: 2.9,
    stripeFeeFixed: 0.3,
    productProfessionalPercent: 85,
    productShopPercent: 12,
    trialPeriodDays: 0,
    migrationMaxGoLiveScheduleDays: 0,
    /**
     * THE RETIRED USAGE-BASED FIELDS ARE GONE FROM THE PLACEHOLDER (2026-11-01).
     *
     * They were carrying `5` here, which pre-filled an admin form with a fee the platform does not charge and
     * could have been saved back. The product fee the platform ACTUALLY deducts is
     * `platformFeePercentProduct`, which is not defaulted here at all — the form waits for the real settings.
     */
    subscriptionFreelancerMonthly: 25,
    annualDiscountPercent: 15,
    subscriptionGracePeriodDays: 3,
    modelSwitchLockDays: 30,
    subscriptionPricingModel: "base_plus_location",
    subscriptionBasePriceMonthly: 35,
    subscriptionAdditionalLocationPrice: 25,
    maxProductImagesPerProduct: 8,
    payoutSettings: {
      minimumPayoutAmount: 25,
      professionalPayoutHoldHours: 24,
      businessPayoutHoldDays: 7,
      payoutTransferFrequencyHours: 12,
      earningsHoldHours: 72,
      servicePostCompletionHoldMinutes: 180,
      productPostFulfillmentHoldMinutes: 180,
      productDisputeBufferHours: 24,
      temporaryLiabilityReservePercent: 0,
      negativeBalanceOffsetEnabled: true,
      instantPayoutFeePercent: 1.5,
      instantPayoutEnabled: true,
      minHoursBeforeInstantPayout: 1,
    },
    ownerWithdrawalSettings: {
      enabled: false,
      allowedRoles: ["R6"],
      fixedReserveAmount: 2000,
      reservePercent: 10,
      perRunCap: 5000,
      minimumWithdrawalAmount: 25,
      notesRequired: true,
    },
    refundSettings: {
      platformAdminApprovalEnabled: true,
      adminApprovalThresholdAmount: 150,
      adminApprovalRiskRules: {
        alreadyTransferredPayout: true,
        repeatedCancellations: true,
        lateCancellationWindow: true,
      },
      allowAdminForceRefund: true,
      allowAdminForceDeny: true,
      adminRefundSlaMinutes: 120,
    },
    securitySettings: {
      maxLoginAttempts: 5,
      lockoutDurationMinutes: 15,
      otpExpiryMinutes: 10,
      passwordMinLength: 8,
      requirePasswordChange: false,
      sessionTimeoutMinutes: 120,
    },
    schedulingSettings: {
      defaultWorkingHoursStart: "09:00",
      defaultWorkingHoursEnd: "18:00",
      defaultSlotDurationMinutes: 30,
      maxAdvanceBookingDays: 90,
      minBookingNoticeHours: 2,
      allowSameDayBooking: true,
    },
    bookingDefaults: {
      waitlistDefaultDurationDays: 14,
      waitlistMaxDurationDays: 30,
      waitlistManualActionsEnabled: false,
    },
    retentionSettings: {
      bookingHistoryDays: 730,
      transactionHistoryDays: 2555,
      inactiveAccountDays: 365,
      deletedAccountRetentionDays: 30,
    },
    cancellationPolicy: {
      enabled: true,
      policyType: "custom",
      hoursBeforeAppointment: 24,
      cancellationFeePercent: 50,
      noShowFeePercent: 100,
      allowRescheduling: true,
      rescheduleWindowHours: 24,
      feeTiers: [],
    },
    cancellationFeeDistribution: {
      freelancerPlatform: 0.4,
      freelancerShare: 0.6,
      shopPlatform: 0.4,
      shopOwner: 0.3,
      shopProfessional: 0.3,
    },
    reschedulePolicy: {
      enabled: true,
      lateRescheduleFeePercent: "",
      platformSharePercentOfFee: 50,
    },
    smsMarketing: {
      enabled: true,
      monthlyCreditsPerShop: 50,
      pricing: {
        currency: "USD",
        basePrice: 0.05,
        minimumPurchase: 100,
        bulkDiscounts: [
          { minCredits: 1000, pricePerCredit: 0.045, discountPercent: 10 },
          { minCredits: 5000, pricePerCredit: 0.04, discountPercent: 20 },
        ],
      },
      lowCreditThreshold: 10,
      urgentCreditThreshold: 3,
      autoReplenish: {
        enabled: true,
        defaultThreshold: 100,
        defaultAmount: 500,
      },
    },
    boothRenterSettings: {
      enabled: false,
      subscriptionMonthly: 10,
      businessMonthlyFeePerRenter: null,
      stripeR7MonthlyPriceId: "",
      rentPayment: {
        applicationFeePercent: 0,
        minRentAmount: 5000,
        maxRentAmount: 200000,
        allowedBillingCycles: ["weekly", "biweekly", "monthly"],
        gracePeriodDays: 3,
        maxFailedAttempts: 3,
      },
      offerDefaults: {
        maxSeatsPerOffer: 10,
        offerExpirationDays: 90,
        referralCodeLength: 12,
      },
      defaultShowOnSalonPage: true,
      r7CanSellProducts: true,
      maxBoothRentalsPerR7: 5,
      trackBookingSource: true,
    },
    lookWatermark: {
      logoUrl: "",
      mimeType: "image/png",
      storageProvider: "",
      updatedAt: null,
    },
    homepageSettings: {
      rankingEngineEnabled: true,
      nearMeSectionEnabled: true,
      noDupEnabled: true,
      diversityEnabled: true,
      productPickupFilterEnabled: false,
      productPickupRadiusMiles: 25,
      publicSearchRadiusOptions: [5, 10, 25, 50],
      publicSearchDefaultRadiusMiles: 10,
      addressAutocompleteCountryCode: "US",
    },
    productsPageHero: {
      enabled: true,
      autoRotateMs: 4500,
      slides: [
        {
          id: "slide-1",
          imageUrl: "",
          title: "Discover Beauty That Performs",
          subtitle:
            "Platform-curated products trusted by verified professionals",
          ctaLabel: "",
          ctaUrl: "",
          isActive: true,
          position: 1,
        },
        {
          id: "slide-2",
          imageUrl: "",
          title: "From Routine To Results",
          subtitle:
            "Find high-performing essentials for hair, skin, and wellness",
          ctaLabel: "",
          ctaUrl: "",
          isActive: true,
          position: 2,
        },
        {
          id: "slide-3",
          imageUrl: "",
          title: "New Season, New Favorites",
          subtitle:
            "Fresh picks, standout formulas, and must-have upgrades",
          ctaLabel: "",
          ctaUrl: "",
          isActive: true,
          position: 3,
        },
        {
          id: "slide-4",
          imageUrl: "",
          title: "Clean Beauty, Real Results",
          subtitle:
            "Browse clean formulations selected for quality and performance",
          ctaLabel: "",
          ctaUrl: "",
          isActive: true,
          position: 4,
        },
        {
          id: "slide-5",
          imageUrl: "",
          title: "Editor Picks This Week",
          subtitle:
            "Trending products chosen by platform editors and top stylists",
          ctaLabel: "",
          ctaUrl: "",
          isActive: true,
          position: 5,
        },
      ],
    },
    storefrontGating: {
      enabled: true,
      rolloutStartedAt: new Date().toISOString(),
      enforcementMode: "full",
      defaultGraceDays: 30,
      existingSellerDefaultPolicy: "full",
      newSellerDefaultPolicy: "block_new",
      requirements: {
        primaryCategories: {
          enabled: true,
          policy: "block_new",
          newSellerPolicy: "block_new",
          existingSellerPolicy: "full",
          graceDays: 30,
          introducedAt: null,
          enforceAfter: null,
        },
        policyTerms: {
          enabled: true,
          policy: "block_new",
          newSellerPolicy: "block_new",
          existingSellerPolicy: "full",
          graceDays: 30,
          introducedAt: null,
          enforceAfter: null,
        },
        policyRefund: {
          enabled: true,
          policy: "block_new",
          newSellerPolicy: "block_new",
          existingSellerPolicy: "full",
          graceDays: 30,
          introducedAt: null,
          enforceAfter: null,
        },
        policyCancellation: {
          enabled: true,
          policy: "block_new",
          newSellerPolicy: "block_new",
          existingSellerPolicy: "full",
          graceDays: 30,
          introducedAt: null,
          enforceAfter: null,
        },
      },
    },
    storefrontSettings: {
      storefrontHandleChangeCooldownDays: null,
    },
  });

  // Fetch settings from API
  const fetchSettings = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      setMessage({ type: "error", text: "Not authenticated" });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.settings) {
        setSettings(response.data.settings);
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to load settings" });
      // Set default settings if fetch fails
      setSettings(getDefaultSettings());
    } finally {
      setLoading(false);
    }
  }, []);

  // Save settings to API
  const saveSettings = useCallback(
    async (settingsToSave = settings) => {
      if (!settingsToSave) {
        setMessage({ type: "error", text: "No settings to save" });
        return false;
      }

      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        setMessage({ type: "error", text: "Not authenticated" });
        return false;
      }

      setSaving(true);
      setMessage({ type: "", text: "" });
      try {
        const response = await axios.put(
          `${API_BASE_URL}/api/admin/settings`,
          settingsToSave,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.data.message || response.data.settings) {
          // Update state with returned settings
          if (response.data.settings) {
            setSettings(response.data.settings);
            setHasUnsavedChanges(false);
          }

          setMessage({
            type: "success",
            text: "Settings updated successfully!",
          });

          // Clear success message after 5 seconds
          setTimeout(() => {
            setMessage({ type: "", text: "" });
          }, 5000);

          return true;
        }
      } catch (error) {
        setMessage({
          type: "error",
          text: error.response?.data?.message || "Failed to update settings",
        });
        return false;
      } finally {
        setSaving(false);
      }
    },
    [settings],
  );

  // Update settings (local state only)
  const updateSettings = useCallback((updates) => {
    setSettings((prev) => ({
      ...prev,
      ...updates,
    }));
    setHasUnsavedChanges(true);
  }, []);

  // Reset to last saved state
  const resetSettings = useCallback(() => {
    fetchSettings();
    setHasUnsavedChanges(false);
  }, [fetchSettings]);

  // Initial load
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    loading,
    saving,
    message,
    hasUnsavedChanges,
    setMessage,
    updateSettings,
    saveSettings,
    resetSettings,
    fetchSettings,
  };
};
